import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Card,
  CardContent,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  InputAdornment,
  Switch,
  FormControlLabel,
  Avatar,
  Tooltip,
  Divider,
  Rating,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  useMediaQuery,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Visibility,
  Search,
  Refresh,
  CloudUpload,
  Hotel,
  Bathtub,
  Wifi,
  AcUnit,
  Tv,
  LocalParking,
  FitnessCenter,
  Pool,
  Restaurant,
  RoomService,
  BusinessCenter,
  FamilyRestroom,
  Balcony,
  Kitchen,
  CoffeeMaker,
  LocalLaundryService,
  Check,
  Close,
  Person,
  CreditCard,
  CheckCircle,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../../../components/sidebars/Sidebar_hotelAdmin';
import TopNav from '../../../components/TopNav_hotelAdmin';

// Styled Components
const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const FileUploadBox = styled(Paper)(({ theme, hasImage }) => ({
  border: `2px dashed ${hasImage ? theme.palette.success.main : theme.palette.primary.main}`,
  borderRadius: '12px',
  padding: theme.spacing(3),
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  backgroundColor: hasImage ? theme.palette.success.light + '20' : theme.palette.background.paper,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    borderColor: hasImage ? theme.palette.success.dark : theme.palette.primary.dark,
    transform: 'translateY(-2px)',
  },
}));

const RoomCard = styled(Card)(({ theme }) => ({
  border: `2px solid ${theme.palette.divider}`,
  borderRadius: '16px',
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
    borderColor: theme.palette.primary.main,
  },
}));

const StatusBadge = styled(Box)(({ theme, status }) => {
  const getColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'available':
        return theme.palette.success.main;
      case 'occupied':
        return theme.palette.error.main;
      case 'maintenance':
        return theme.palette.warning.main;
      case 'cleaning':
        return theme.palette.info.main;
      default:
        return theme.palette.grey[500];
    }
  };

  return {
    backgroundColor: getColor(status),
    color: 'white',
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: 'bold',
    display: 'inline-block',
  };
});

const ImagePreview = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '200px',
  borderRadius: '12px',
  backgroundColor: theme.palette.grey[200],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  position: 'relative',
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'opacity 0.3s ease',
  },
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  '& .MuiTabs-indicator': {
    backgroundColor: theme.palette.primary.main,
    height: 3,
    borderRadius: '3px 3px 0 0',
  },
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '14px',
  minWidth: 120,
  color: theme.palette.text.secondary,
  '&.Mui-selected': {
    color: theme.palette.primary.main,
  },
  '&:hover': {
    color: theme.palette.primary.main,
    backgroundColor: 'transparent',
  },
}));

const ReviewCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.action.hover} 100%)`,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '12px',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[8],
  },
}));

// Available amenities with icons
const availableAmenities = [
  { id: 1, name: 'WiFi', icon: <Wifi /> },
  { id: 2, name: 'Air Conditioning', icon: <AcUnit /> },
  { id: 3, name: 'TV', icon: <Tv /> },
  { id: 4, name: 'Private Bathroom', icon: <Bathtub /> },
  { id: 5, name: 'Parking', icon: <LocalParking /> },
  { id: 6, name: 'Swimming Pool', icon: <Pool /> },
  { id: 7, name: 'Fitness Center', icon: <FitnessCenter /> },
  { id: 8, name: 'Restaurant', icon: <Restaurant /> },
  { id: 9, name: 'Room Service', icon: <RoomService /> },
  { id: 10, name: 'Business Center', icon: <BusinessCenter /> },
  { id: 11, name: 'Family Friendly', icon: <FamilyRestroom /> },
  { id: 12, name: 'Balcony', icon: <Balcony /> },
  { id: 13, name: 'Kitchen', icon: <Kitchen /> },
  { id: 14, name: 'Coffee Maker', icon: <CoffeeMaker /> },
  { id: 15, name: 'Laundry', icon: <LocalLaundryService /> },
];

const roomTypes = [
  'Standard Room',
  'Deluxe Room',
  'Executive Suite',
  'Presidential Suite',
  'Family Room',
  'Honeymoon Suite',
  'Business Room',
  'Accessible Room',
];

const roomStatuses = ['Available', 'Occupied', 'Maintenance', 'Cleaning'];

// API base URL - adjust according to your setup
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Safe Image Component
const SafeImage = ({ src, alt, fallback = <Hotel sx={{ fontSize: 60, color: 'grey.400' }} />, ...props }) => {
  const [imageError, setImageError] = useState(false);
  const [imageUrl, setImageUrl] = useState('');

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    
    // If it's already a full URL, return as is
    if (imagePath.startsWith('http')) return imagePath;
    
    const serverRoot = process.env.REACT_APP_SERVER_ROOT;
    const cleanPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
    
    return `${serverRoot}${cleanPath}`;
  };

  useEffect(() => {
    if (src) {
      const resolvedUrl = getImageUrl(src);
      setImageUrl(resolvedUrl);
      setImageError(false);
      
      // Preload image to check if it exists
      const img = new Image();
      img.onload = () => setImageError(false);
      img.onerror = () => setImageError(true);
      img.src = resolvedUrl;
    } else {
      setImageError(true);
    }
  }, [src]);

  if (imageError || !imageUrl) {
    return fallback;
  }

  return (
    <img
      src={imageUrl}
      alt={alt}
      onError={() => setImageError(true)}
      {...props}
    />
  );
};

const HotelRoomManagement = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width:768px)');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    status: '',
    type: '',
    minPrice: '',
    maxPrice: '',
  });

  // State management
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [amenities, setAmenities] = useState([]);
  const [statistics, setStatistics] = useState(null);
  
  const [openDialog, setOpenDialog] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [uploadedImages, setUploadedImages] = useState([]);
  const [uploading, setUploading] = useState(false);

  // Get hotel code from user context or localStorage - IMPROVED VERSION
  const getHotelCode = () => {
    // Retrieve hotel data from localStorage (set during login)
    const storedProfile = JSON.parse(localStorage.getItem('userProfile')) || {};
    const hotelCode = storedProfile.hotelcode || storedProfile.hotel_code || storedProfile.hotelCode || '';
    
    console.log('Stored Profile:', storedProfile);
    console.log('Extracted Hotel Code:', hotelCode);
    
    if (!hotelCode) {
      console.warn('No hotel code found in user profile, using fallback');
      return 'HOTEL_001'; // Fallback code
    }
    
    return hotelCode;
  };

  const [roomForm, setRoomForm] = useState({
    hotel_code: getHotelCode(), // Initialize with actual hotel code
    name: '',
    type: '',
    number: '',
    price: '',
    capacity: '',
    size: '',
    status: 'Available',
    description: '',
    amenities: [],
    is_active: true,
  });

  // Enhanced image URL resolver with debugging
  const getImageUrl = (imagePath) => {
    if (!imagePath) {
      console.log('No image path provided');
      return null;
    }
    
    console.log('Original image path:', imagePath);
    
    // If it's already a full URL, return as is
    if (imagePath.startsWith('http')) {
      console.log('Full URL detected:', imagePath);
      return imagePath;
    }
    
    // If it's a relative path starting with /uploads
    if (imagePath.startsWith('/uploads/')) {
      const url = `${API_BASE_URL}${imagePath}`;
      console.log('Relative path with slash - resolved to:', url);
      return url;
    }
    
    // If it starts with uploads (without slash)
    if (imagePath.startsWith('uploads/')) {
      const url = `${API_BASE_URL}/${imagePath}`;
      console.log('Relative path without slash - resolved to:', url);
      return url;
    }
    
    // For paths with ../ or complex relative paths
    if (imagePath.includes('uploads/')) {
      const filename = imagePath.split('/').pop();
      const url = `${API_BASE_URL}/uploads/${filename}`;
      console.log('Complex path - extracted filename:', filename, 'resolved to:', url);
      return url;
    }
    
    // Default case - assume it's just a filename
    const url = `${API_BASE_URL}/uploads/${imagePath}`;
    console.log('Default case - resolved to:', url);
    return url;
  };

  // API service functions - FIXED VERSION
  const api = {
    // Get all rooms
    getRooms: async (params = {}) => {
      const response = await axios.get(`${API_BASE_URL}/hotel/rooms/index.php`, { 
        params: { hotel_code: getHotelCode(), ...params } 
      });
      return response.data;
    },

    // Get single room - FIXED
    getRoom: async (id) => {
      const response = await axios.get(`${API_BASE_URL}/hotel/rooms/get.php`, {
        params: { id, hotel_code: getHotelCode() }
      });
      return response.data;
    },

    // Create room - USING MULTIPART/FORM-DATA
    createRoom: async (roomData, imageFiles = []) => {
      const formData = new FormData();
      
      // Append room data as JSON string
      formData.append('room_data', JSON.stringify(roomData));
      
      // Append image files
      imageFiles.forEach((image, index) => {
        if (image.file) {
          console.log(`Appending image ${index}:`, image.file.name, image.file.size, image.file.type);
          formData.append('images[]', image.file);
        }
      });

      const response = await axios.post(`${API_BASE_URL}/hotel/rooms/create.php`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    },

    updateRoom: async (roomData, imageFiles = []) => {
      const formData = new FormData();
      
      // Append room data as JSON string
      formData.append('room_data', JSON.stringify(roomData));
      
      // Append image files
      imageFiles.forEach((image, index) => {
        if (image.file) {
          console.log(`Appending image ${index}:`, image.file.name, image.file.size, image.file.type);
          formData.append('images[]', image.file);
        }
      });

      const response = await axios.post(`${API_BASE_URL}/hotel/rooms/update.php`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    },

    // Delete room
    deleteRoom: async (id) => {
      const response = await axios.delete(`${API_BASE_URL}/hotel/rooms/delete.php`, {
        params: { id, hotel_code: getHotelCode() }
      });
      return response.data;
    },

    // Toggle room status - FIXED
    toggleRoomStatus: async (id, currentStatus) => {
      const response = await axios.put(`${API_BASE_URL}/hotel/rooms/toggle-status.php`, {
        id,
        hotel_code: getHotelCode(),
        is_active: !currentStatus
      }, {
        headers: {
          'Content-Type': 'application/json',
        }
      });
      return response.data;
    },

    // Update room status - NEW FUNCTION
    updateRoomStatus: async (id, status) => {
      const response = await axios.put(`${API_BASE_URL}/hotel/rooms/update-status.php`, {
        id,
        hotel_code: getHotelCode(),
        status
      }, {
        headers: {
          'Content-Type': 'application/json',
        }
      });
      return response.data;
    },

    // Get amenities
    getAmenities: async () => {
      const response = await axios.get(`${API_BASE_URL}/hotel/rooms/amenities.php`);
      return response.data;
    },

    // Get statistics
    getStatistics: async () => {
      const response = await axios.get(`${API_BASE_URL}/hotel/rooms/statistics.php`, {
        params: { hotel_code: getHotelCode() }
      });
      return response.data;
    }
  };

  // Load initial data
  useEffect(() => {
    loadRooms();
    loadAmenities();
    loadStatistics();
  }, []);

  // Load rooms when filters change
  useEffect(() => {
    loadRooms();
  }, [filters, searchQuery]);

  const loadRooms = async () => {
    setLoading(true);
    try {
      const params = {};
      if (searchQuery) params.search = searchQuery;
      if (filters.status) params.status = filters.status;
      if (filters.type) params.type = filters.type;
      if (filters.minPrice) params.min_price = filters.minPrice;
      if (filters.maxPrice) params.max_price = filters.maxPrice;

      const data = await api.getRooms(params);
      if (data.success) {
        console.log('Loaded rooms:', data.rooms);
        setRooms(data.rooms || []);
      } else {
        showSnackbar(data.error || 'Failed to load rooms', 'error');
      }
    } catch (error) {
      console.error('Error loading rooms:', error);
      showSnackbar('Failed to load rooms. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadAmenities = async () => {
    try {
      const data = await api.getAmenities();
      if (data.success) {
        setAmenities(data.amenities || []);
      }
    } catch (error) {
      console.error('Error loading amenities:', error);
    }
  };

  const loadStatistics = async () => {
    try {
      const data = await api.getStatistics();
      if (data.success) {
        setStatistics(data.statistics);
      }
    } catch (error) {
      console.error('Error loading statistics:', error);
    }
  };

  const tabLabels = [
    { label: 'Room Overview', icon: <Hotel /> },
    { label: 'Add New Room', icon: <Add /> },
    { label: 'Room Analytics', icon: <Visibility /> },
  ];

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // FIXED: Handle Open Dialog with proper room data loading
  const handleOpenDialog = async (room = null) => {
    if (room) {
      try {
        setLoading(true);
        // Fetch complete room details including images and amenities
        const roomDetails = await api.getRoom(room.id);
        
        if (roomDetails.success) {
          setEditingRoom(roomDetails.room);
          setRoomForm({
            ...roomDetails.room,
            hotel_code: getHotelCode(),
          });
          
          // Handle images properly
          const roomImages = roomDetails.room.images || [];
          setUploadedImages(roomImages.map(img => ({ 
            url: img,
            preview: getImageUrl(img) 
          })));
        } else {
          showSnackbar('Failed to load room details', 'error');
        }
      } catch (error) {
        console.error('Error loading room details:', error);
        showSnackbar('Failed to load room details', 'error');
      } finally {
        setLoading(false);
      }
    } else {
      setEditingRoom(null);
      setRoomForm({
        hotel_code: getHotelCode(),
        name: '',
        type: '',
        number: '',
        price: '',
        capacity: '',
        size: '',
        status: 'Available',
        description: '',
        amenities: [],
        is_active: true,
      });
      setUploadedImages([]);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingRoom(null);
    setUploadedImages([]);
  };

  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    setRoomForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAmenityToggle = (amenityId) => {
    setRoomForm(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenityId)
        ? prev.amenities.filter(id => id !== amenityId)
        : [...prev.amenities, amenityId]
    }));
  };

  const handleImageUpload = (event) => {
    const files = Array.from(event.target.files);
    const newImages = files.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      isNew: true
    }));
    setUploadedImages(prev => [...prev, ...newImages]);
  };

  const removeImage = (index) => {
    setUploadedImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!roomForm.name || !roomForm.type || !roomForm.number || !roomForm.price) {
      showSnackbar('Please fill in all required fields', 'error');
      return;
    }

    setUploading(true);
    try {
      // Prepare room data
      const roomData = {
        hotel_code: roomForm.hotel_code || getHotelCode(),
        name: roomForm.name,
        type: roomForm.type,
        number: roomForm.number,
        price: parseFloat(roomForm.price),
        capacity: parseInt(roomForm.capacity),
        size: roomForm.size ? parseFloat(roomForm.size) : null,
        status: roomForm.status,
        description: roomForm.description || '',
        amenities: roomForm.amenities || [],
        is_active: Boolean(roomForm.is_active),
      };

      console.log('Sending room data:', roomData);

      let result;
      if (editingRoom) {
        // For updates, include the ID
        roomData.id = editingRoom.id;
        result = await api.updateRoom(roomData, uploadedImages);
      } else {
        result = await api.createRoom(roomData, uploadedImages);
      }

      if (result.success) {
        showSnackbar(editingRoom ? 'Room updated successfully!' : 'Room added successfully!', 'success');
        await loadRooms();
        await loadStatistics();
        handleCloseDialog();
        setActiveTab(0);
      } else {
        showSnackbar(result.error || 'Operation failed', 'error');
      }
    } catch (error) {
      console.error('Error saving room:', error);
      console.error('Error response:', error.response?.data);
      showSnackbar(
        error.response?.data?.error || 
        'Failed to save room. Please try again.', 
        'error'
      );
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteRoom = async (roomId) => {
    if (window.confirm('Are you sure you want to delete this room?')) {
      try {
        const result = await api.deleteRoom(roomId);
        if (result.success) {
          await loadRooms();
          await loadStatistics();
          showSnackbar('Room deleted successfully!', 'success');
        } else {
          showSnackbar(result.error || 'Failed to delete room', 'error');
        }
      } catch (error) {
        console.error('Error deleting room:', error);
        showSnackbar('Failed to delete room. Please try again.', 'error');
      }
    }
  };

  // FIXED: Handle toggle status
  const handleToggleStatus = async (roomId, currentStatus) => {
    try {
      const result = await api.toggleRoomStatus(roomId, currentStatus);
      if (result.success) {
        await loadRooms();
        showSnackbar('Room status updated!', 'success');
      } else {
        showSnackbar(result.error || 'Failed to update status', 'error');
      }
    } catch (error) {
      console.error('Error toggling room status:', error);
      showSnackbar('Failed to update room status. Please try again.', 'error');
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleFilterChange = (filter) => (event) => {
    setFilters(prev => ({
      ...prev,
      [filter]: event.target.value
    }));
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRefresh = () => {
    loadRooms();
    loadStatistics();
    showSnackbar('Data refreshed successfully!', 'success');
  };

  const filteredRooms = rooms.filter(room => 
    room.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    room.number.toLowerCase().includes(searchQuery.toLowerCase()) ||
    room.type.toLowerCase().includes(searchQuery.toLowerCase())
  ).filter(room => 
    (filters.status ? room.status === filters.status : true) &&
    (filters.type ? room.type === filters.type : true) &&
    (filters.minPrice ? room.price >= parseInt(filters.minPrice) : true) &&
    (filters.maxPrice ? room.price <= parseInt(filters.maxPrice) : true)
  );

  const getAmenityIcon = (amenityId) => {
    const amenity = availableAmenities.find(a => a.id === amenityId);
    return amenity ? amenity.icon : <Hotel />;
  };

  const getAmenityName = (amenityId) => {
    const amenity = availableAmenities.find(a => a.id === amenityId);
    return amenity ? amenity.name : 'Unknown';
  };

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userProfile');
    navigate('/Entri');
  };

  const handleNotificationClick = () => {
    alert('You have new notifications!');
  };

  // FIXED: Calculate statistics from rooms data if API statistics are not available
  const calculateStatistics = () => {
    const totalRooms = rooms.length;
    const availableRooms = rooms.filter(room => room.status === 'Available').length;
    const occupiedRooms = rooms.filter(room => room.status === 'Occupied').length;
    const maintenanceRooms = rooms.filter(room => room.status === 'Maintenance').length;
    const cleaningRooms = rooms.filter(room => room.status === 'Cleaning').length;
    const avgPrice = rooms.length > 0 ? 
      rooms.reduce((sum, room) => sum + parseFloat(room.price), 0) / rooms.length : 0;

    return {
      total_rooms: totalRooms,
      available_rooms: availableRooms,
      occupied_rooms: occupiedRooms,
      maintenance_rooms: maintenanceRooms,
      cleaning_rooms: cleaningRooms,
      avg_price: avgPrice
    };
  };

  const displayStats = statistics || calculateStatistics();

  // FIXED: Room Type Distribution Calculation
  const getRoomTypeDistribution = () => {
    const distribution = rooms.reduce((acc, room) => {
      acc[room.type] = (acc[room.type] || 0) + 1;
      return acc;
    }, {});
    return distribution;
  };

  const renderRoomOverviewTab = () => (
    <Box>
      {/* Filters and Search */}
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search rooms by name, number, or type..."
              value={searchQuery}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
          <FormControl fullWidth sx={{ minWidth: 200 }}> {/* Force minimum width */}
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                onChange={handleFilterChange('status')}
                label="Status"
              >
                <MenuItem value="">All Status</MenuItem>
                {roomStatuses.map(status => (
                  <MenuItem key={status} value={status}>{status}</MenuItem>
                ))}
              </Select>
            </FormControl>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
          <FormControl fullWidth sx={{ minWidth: 200 }}> {/* Force minimum width */}
            <FormControl fullWidth>
              <InputLabel>Room Type</InputLabel>
              <Select
                value={filters.type}
                onChange={handleFilterChange('type')}
                label="Room Type"
              >
                <MenuItem value="">All Types</MenuItem>
                {roomTypes.map(type => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </Select>
            </FormControl>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
          
            <TextField
              fullWidth
              label="Min Price"
              type="number"
              value={filters.minPrice}
              onChange={handleFilterChange('minPrice')}
              InputProps={{ startAdornment: <InputAdornment position="start"></InputAdornment> }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                variant="outlined" 
                startIcon={<Refresh />}
                onClick={handleRefresh}
                disabled={loading}
              >
                Refresh
              </Button>
             
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Rooms Grid View */}
      <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
        All Rooms ({filteredRooms.length})
      </Typography>

      {!loading && filteredRooms.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="textSecondary">
            No rooms found
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            {searchQuery || Object.values(filters).some(f => f) 
              ? 'Try adjusting your search or filters' 
              : 'Get started by adding your first room'
            }
          </Typography>
          {!searchQuery && !Object.values(filters).some(f => f) && (
            <Button 
              variant="contained" 
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
              sx={{ mt: 2 }}
            >
              Add First Room
            </Button>
          )}
        </Paper>
      )}

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {filteredRooms.map((room) => (
          <Grid item xs={12} md={6} lg={4} key={room.id}>
            <RoomCard>
              <Box sx={{ position: 'relative' }}>
                <ImagePreview>
                  <SafeImage 
                    src={room.images && room.images[0]} 
                    alt={room.name}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    fallback={<Hotel sx={{ fontSize: 60, color: 'grey.400' }} />}
                  />
                </ImagePreview>
                <Box sx={{ position: 'absolute', top: 12, right: 12, display: 'flex', gap: 1 }}>
                  <StatusBadge status={room.status}>
                    {room.status}
                  </StatusBadge>
                  <Chip
                    label={`$${room.price}`}
                    color="primary"
                    size="small"
                    sx={{ backgroundColor: 'white', color: 'primary.main', fontWeight: 'bold' }}
                  />
                </Box>
                <Box sx={{ position: 'absolute', top: 12, left: 12 }}>
                  <Rating value={room.rating} precision={0.5} size="small" readOnly />
                </Box>
              </Box>
              
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      {room.name}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Room {room.number} • {room.type}
                    </Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={room.is_active}
                        onChange={() => handleToggleStatus(room.id, room.is_active)}
                        color="success"
                        size="small"
                        disabled={loading}
                      />
                    }
                    label=""
                  />
                </Box>

                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  {room.description || 'No description available'}
                </Typography>

                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                  <Chip icon={<Person />} label={`${room.capacity} guests`} size="small" />
                  {room.size && <Chip icon={<Bathtub />} label={`${room.size} m²`} size="small" />}
                </Box>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                  {room.amenities && room.amenities.slice(0, 4).map(amenityId => (
                    <Tooltip key={amenityId} title={getAmenityName(amenityId)}>
                      <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.light' }}>
                        {getAmenityIcon(amenityId)}
                      </Avatar>
                    </Tooltip>
                  ))}
                  {room.amenities && room.amenities.length > 4 && (
                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'grey.300' }}>
                      <Typography variant="caption">+{room.amenities.length - 4}</Typography>
                    </Avatar>
                  )}
                </Box>

                <Box sx={{ display: 'flex', gap: 1 }}>
                  <IconButton 
                    size="small" 
                    color="primary"
                    onClick={() => handleOpenDialog(room)}
                    disabled={loading}
                  >
                    <Edit />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    color="info"
                    onClick={() => handleOpenDialog(room)}
                    disabled={loading}
                  >
                    <Visibility />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    color="error"
                    onClick={() => handleDeleteRoom(room.id)}
                    disabled={loading}
                  >
                    <Delete />
                  </IconButton>
                </Box>
              </CardContent>
            </RoomCard>
          </Grid>
        ))}
      </Grid>

      {/* Rooms Table View */}
      <Paper elevation={3}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'primary.main' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Room</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Type</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Number</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Price</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Capacity</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Rating</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Active</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRooms
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((room) => (
                  <TableRow key={room.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar
                          variant="rounded"
                          sx={{ width: 50, height: 50 }}
                          src={room.images && room.images[0] ? getImageUrl(room.images[0]) : ''}
                        >
                          <Hotel />
                        </Avatar>
                        <Box>
                          <Typography fontWeight="medium">{room.name}</Typography>
                          <Typography variant="body2" color="textSecondary">
                            {room.size || 'N/A'} m²
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{room.type}</TableCell>
                    <TableCell>
                      <Chip label={room.number} variant="outlined" size="small" />
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight="bold" color="primary">
                        ₦{room.price}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        icon={<Person />} 
                        label={`${room.capacity} guests`} 
                        size="small" 
                      />
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={room.status}>
                        {room.status}
                      </StatusBadge>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Rating value={room.rating} size="small" readOnly />
                        <Typography variant="body2">{room.rating}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={room.is_active}
                        onChange={() => handleToggleStatus(room.id, room.is_active)}
                        color="success"
                        size="small"
                        disabled={loading}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton 
                          size="small" 
                          color="primary"
                          onClick={() => handleOpenDialog(room)}
                          disabled={loading}
                        >
                          <Edit />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          color="info"
                          onClick={() => handleOpenDialog(room)}
                          disabled={loading}
                        >
                          <Visibility />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          color="error"
                          onClick={() => handleDeleteRoom(room.id)}
                          disabled={loading}
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredRooms.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Box>
  );

  const renderAddRoomTab = () => (
  <Paper elevation={3} sx={{ p: 4, mt: 2 }}>
    <Typography variant="h4" gutterBottom align="center" color="primary" sx={{ mb: 1 }}>
      {editingRoom ? 'Edit Room' : 'Add New Room'}
    </Typography>
    <Typography variant="subtitle1" gutterBottom align="center" color="textSecondary" sx={{ mb: 4 }}>
      {editingRoom ? 'Update room details' : 'Create a new room listing with all necessary details'}
    </Typography>

    <Grid container spacing={4}>
      {/* Section 1: Basic Information */}
      <Grid item xs={12}>
        <Typography variant="h5" gutterBottom color="primary" sx={{ fontWeight: 600 }}>
          Basic Information
        </Typography>
        <Divider sx={{ mb: 3 }} />
      </Grid>

      {/* Room Identification */}
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Room Name *"
          value={roomForm.name}
          onChange={handleInputChange('name')}
          placeholder="e.g., Deluxe Ocean View"
          required
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Room Number *"
          value={roomForm.number}
          onChange={handleInputChange('number')}
          placeholder="e.g., 301"
          required
        />
      </Grid>

      {/* Room Specifications */}
      <Grid item xs={12} md={4}>
      <FormControl fullWidth sx={{ minWidth: 200 }}> {/* Force minimum width */}
        <FormControl fullWidth required>
          <InputLabel>Room Type *</InputLabel>
          <Select
            value={roomForm.type}
            onChange={handleInputChange('type')}
            label="Room Type *"
          >
            {roomTypes.map(type => (
              <MenuItem key={type} value={type}>{type}</MenuItem>
            ))}
          </Select>
        </FormControl>
        </FormControl>
      </Grid>

      <Grid item xs={12} md={4}>
      <FormControl fullWidth sx={{ minWidth: 200 }}> {/* Force minimum width */}
        <TextField
          fullWidth
          label="Guest Capacity *"
          type="number"
          value={roomForm.capacity}
          onChange={handleInputChange('capacity')}
          InputProps={{ inputProps: { min: 1, max: 10 } }}
          required
        />
        </FormControl>
      </Grid>

      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="Room Size (m²)"
          type="number"
          value={roomForm.size}
          onChange={handleInputChange('size')}
          InputProps={{ endAdornment: <InputAdornment position="end">m²</InputAdornment> }}
        />
      </Grid>

      {/* Pricing & Status */}
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Price per Night *"
          type="number"
          value={roomForm.price}
          onChange={handleInputChange('price')}
          InputProps={{ startAdornment: <InputAdornment position="start">₦</InputAdornment> }}
          required
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <InputLabel>Status</InputLabel>
          <Select
            value={roomForm.status}
            onChange={handleInputChange('status')}
            label="Status"
          >
            {roomStatuses.map(status => (
              <MenuItem key={status} value={status}>{status}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      {/* Description */}
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Room Description"
          multiline
          rows={3}
          value={roomForm.description}
          onChange={handleInputChange('description')}
          placeholder="Describe the room features, view, and special amenities..."
        />
      </Grid>

      {/* Section 2: Visual Content */}
      <Grid item xs={12}>
        <Typography variant="h5" gutterBottom color="primary" sx={{ fontWeight: 600, mt: 2 }}>
          Room Images
        </Typography>
        <Divider sx={{ mb: 3 }} />
        
        <FileUploadBox 
          hasImage={uploadedImages.length > 0}
          onClick={() => document.getElementById('room-images-upload').click()}
        >
          <input
            id="room-images-upload"
            type="file"
            multiple
            accept="image/*"
            onChange={handleImageUpload}
            style={{ display: 'none' }}
          />
          <CloudUpload sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Upload Room Images
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Click to upload or drag and drop
          </Typography>
        </FileUploadBox>

        {/* Image Previews */}
        {uploadedImages.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
              Uploaded Images ({uploadedImages.filter(img => img.preview || img.url).length})
            </Typography>
            <Grid container spacing={2}>
              {uploadedImages
                .filter(image => image.preview || image.url)
                .map((image, index) => (
                  <Grid item xs={6} sm={4} md={3} key={index}>
                    <Box sx={{ position: 'relative' }}>
                      <ImagePreview>
                        <img 
                          src={image.preview || image.url} 
                          alt={`Room ${index + 1}`}
                          style={{ 
                            width: '100%', 
                            height: '120px', 
                            objectFit: 'cover',
                            borderRadius: '8px'
                          }}
                          onError={(e) => {
                            console.log('Image failed to load:', image);
                            e.target.style.display = 'none';
                          }}
                        />
                      </ImagePreview>
                      <IconButton
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          backgroundColor: 'error.main',
                          color: 'white',
                          '&:hover': {
                            backgroundColor: 'error.dark',
                          },
                        }}
                        onClick={() => removeImage(index)}
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    </Box>
                  </Grid>
                ))}
            </Grid>
          </Box>
        )}
      </Grid>

      {/* Section 3: Amenities */}
      <Grid item xs={12}>
        <Typography variant="h5" gutterBottom color="primary" sx={{ fontWeight: 600, mt: 2 }}>
          Room Amenities
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <Grid container spacing={2}>
          {availableAmenities.map(amenity => (
            <Grid item xs={6} sm={4} md={3} lg={2} key={amenity.id}>
              <Card
                variant="outlined"
                sx={{
                  cursor: 'pointer',
                  borderColor: roomForm.amenities.includes(amenity.id) ? 'primary.main' : 'divider',
                  backgroundColor: roomForm.amenities.includes(amenity.id)
                    ? 'rgba(25, 118, 210, 0.1)'
                    : 'background.paper',
                  transition: 'all 0.2s ease',
                  height: '100%',
                }}
                onClick={() => handleAmenityToggle(amenity.id)}
              >
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexDirection: 'column', textAlign: 'center' }}>
                    <Box sx={{ 
                      color: roomForm.amenities.includes(amenity.id) ? 'primary.main' : 'action.active',
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      {amenity.icon}
                    </Box>
                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                      {amenity.name}
                    </Typography>
                    {roomForm.amenities.includes(amenity.id) && (
                      <Check sx={{ fontSize: 16, color: 'primary.main' }} />
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Grid>

      {/* Action Buttons */}
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 4, pt: 3, borderTop: 1, borderColor: 'divider' }}>
          <Button
            variant="outlined"
            size="large"
            onClick={() => {
              setRoomForm({
                hotel_code: getHotelCode(),
                name: '', type: '', number: '', price: '', capacity: '',
                size: '', status: 'Available', description: '', amenities: [], is_active: true
              });
              setUploadedImages([]);
            }}
            disabled={uploading}
          >
            Clear Form
          </Button>
          <Button
            variant="contained"
            size="large"
            startIcon={uploading ? <CircularProgress size={20} /> : <Add />}
            onClick={handleSubmit}
            disabled={!roomForm.name || !roomForm.type || !roomForm.number || !roomForm.price || uploading}
          >
            {uploading ? 'Saving...' : editingRoom ? 'Update Room' : 'Create Room'}
          </Button>
        </Box>
      </Grid>
    </Grid>
  </Paper>
);

  // FIXED: Analytics Tab with proper data display
  const renderAnalyticsTab = () => {
    const roomTypeDistribution = getRoomTypeDistribution();
    
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="h4" gutterBottom color="primary">
          Room Analytics
        </Typography>
        <Typography variant="subtitle1" color="textSecondary" sx={{ mb: 4 }}>
          Comprehensive insights and performance metrics for your hotel rooms
        </Typography>

        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {[
            {
              title: 'Total Rooms',
              value: displayStats.total_rooms || '0',
              icon: <Hotel />,
              color: 'primary',
              description: 'All rooms in hotel'
            },
            {
              title: 'Available Rooms',
              value: displayStats.available_rooms || '0',
              icon: <CheckCircle />,
              color: 'success',
              description: 'Ready for booking'
            },
            {
              title: 'Occupied Rooms',
              value: displayStats.occupied_rooms || '0',
              icon: <Person />,
              color: 'warning',
              description: 'Currently in use'
            },
            {
              title: 'Under Maintenance',
              value: displayStats.maintenance_rooms || '0',
              icon: <AcUnit />,
              color: 'error',
              description: 'Being repaired'
            }
          ].map((stat, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card sx={{ p: 2, textAlign: 'center' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                    <Avatar sx={{ bgcolor: `${stat.color}.main`, width: 60, height: 60 }}>
                      {stat.icon}
                    </Avatar>
                  </Box>
                  <Typography variant="h4" fontWeight="bold" color={`${stat.color}.main`}>
                    {stat.value}
                  </Typography>
                  <Typography variant="h6" gutterBottom>
                    {stat.title}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {stat.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={3}>
          {/* Room Type Distribution */}
          <Grid item xs={12} md={6}>
            <ReviewCard>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">
                  Room Type Distribution
                </Typography>
                {Object.keys(roomTypeDistribution).length > 0 ? (
                  <Box sx={{ mt: 2 }}>
                    {Object.entries(roomTypeDistribution).map(([type, count]) => (
                      <Box key={type} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">{type}</Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {count} {count === 1 ? 'room' : 'rooms'}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    No room data available
                  </Typography>
                )}
              </CardContent>
            </ReviewCard>
          </Grid>

          {/* Price Statistics */}
          <Grid item xs={12} md={6}>
            <ReviewCard>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">
                  Price Statistics
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Average Price:</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      ₦{displayStats.avg_price ? Number(displayStats.avg_price).toFixed(2) : '0.00'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Highest Price:</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      ₦{rooms.length > 0 ? Math.max(...rooms.map(r => parseFloat(r.price || 0))).toFixed(2) : '0.00'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Lowest Price:</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      ₦{rooms.length > 0 ? Math.min(...rooms.map(r => parseFloat(r.price || 0))).toFixed(2) : '0.00'}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </ReviewCard>
          </Grid>

          {/* Status Overview */}
          <Grid item xs={12}>
            <ReviewCard>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">
                  Room Status Overview
                </Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  {[
                    { status: 'Available', color: 'success', count: displayStats.available_rooms || 0 },
                    { status: 'Occupied', color: 'warning', count: displayStats.occupied_rooms || 0 },
                    { status: 'Maintenance', color: 'error', count: displayStats.maintenance_rooms || 0 },
                    { status: 'Cleaning', color: 'info', count: displayStats.cleaning_rooms || 0 }
                  ].map((item, index) => (
                    <Grid item xs={12} sm={6} md={3} key={index}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <StatusBadge status={item.status}>
                          {item.status}
                        </StatusBadge>
                        <Typography variant="body2" fontWeight="bold">
                          ({item.count})
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </ReviewCard>
          </Grid>
        </Grid>
      </Box>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 0:
        return renderRoomOverviewTab();
      case 1:
        return renderAddRoomTab();
      case 2:
        return renderAnalyticsTab();
      default:
        return renderRoomOverviewTab();
    }
  };

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Sidebar */}
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

      {/* Main Content */}
      <Box
        sx={{
          flexGrow: 1,
          p: 3,
          ml: isMobile ? 0 : sidebarOpen ? 30 : 5,
          mt: 0,
          maxWidth: '1400px',
          mx: 'auto',
        }}
      >
        {/* Top Navigation */}
        <TopNav
          onLogout={handleLogout}
          onNotificationClick={handleNotificationClick}
          toggleSidebar={toggleSidebar}
        />

        {/* Main Content Header */}
        <Paper elevation={2} sx={{ p: 3, mt: 3, backgroundColor: 'primary.main', color: 'white' }}>
          <Typography variant="h4" fontWeight="bold">
            Room Management
          </Typography>
          <Typography variant="subtitle1">
            Manage your hotel rooms, availability, and pricing
          </Typography>
        </Paper>

        {/* Tabs Section */}
        <Paper elevation={2} sx={{ mt: 2 }}>
          <StyledTabs
            value={activeTab}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTabs-flexContainer': {
                justifyContent: 'center',
              },
            }}
          >
            {tabLabels.map((tab, index) => (
              <StyledTab
                key={index}
                label={tab.label}
                icon={tab.icon}
                iconPosition="start"
              />
            ))}
          </StyledTabs>

          {/* Tab Content */}
          <Box sx={{ p: 3 }}>
            {renderTabContent()}
          </Box>
        </Paper>
      </Box>

      {/* Add/Edit Room Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {editingRoom ? 'Edit Room' : 'Add New Room'}
        </DialogTitle>
        <DialogContent>
          {renderAddRoomTab()}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSubmit}
            variant="contained"
            disabled={!roomForm.name || !roomForm.type || !roomForm.number || !roomForm.price || uploading}
          >
            {uploading ? 'Saving...' : editingRoom ? 'Update Room' : 'Create Room'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default HotelRoomManagement;